import { Injectable } from '@angular/core';
import { BehaviorSubject, of, switchMap, tap, map } from 'rxjs';
import { OfferService } from '@/service/offer.service';
import { KeycloakService } from 'keycloak-angular';
import { OfferWithImage } from '@/model/offer-with-image';
import { ManagedUser, User } from '@/model/user';
import { StructuredSearchFilterDto } from '@/model/dto/structured-search-filter.dto';
import { UserService } from '@/service/user.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TrendingCategoriesFacade } from '../store/trending-categories';

@UntilDestroy()
@Injectable()
export class HomeFacade {
  private isLoadingSubject = new BehaviorSubject<boolean>(true);
  private managedUsersOffersSubject = new BehaviorSubject<
    Record<string, OfferWithImage[]>
  >({});
  private managedUsersSubject = new BehaviorSubject<ManagedUser[]>([]);
  private userOffersSubject = new BehaviorSubject<OfferWithImage[]>([]);

  readonly isLoading$ = this.isLoadingSubject.asObservable();
  readonly parentCategories$ = this.trendingCategoriesFacade.parentCategories$;
  readonly allOffers$ = this.trendingCategoriesFacade.allOffers$;
  readonly filteredOffers$ = this.trendingCategoriesFacade.filteredOffers$;
  readonly managedUsersOffers$ = this.managedUsersOffersSubject.asObservable();
  readonly managedUsers$ = this.managedUsersSubject.asObservable();
  readonly userOffers$ = this.userOffersSubject.asObservable();
  readonly selectedCategoryId$ =
    this.trendingCategoriesFacade.selectedCategoryId$;

  constructor(
    private readonly offerService: OfferService,
    private readonly userService: UserService,
    private readonly keycloakService: KeycloakService,
    private readonly trendingCategoriesFacade: TrendingCategoriesFacade,
  ) {
    this.initialize();
  }

  private initialize(): void {
    const isLoggedIn = this.keycloakService.isLoggedIn();

    if (isLoggedIn) {
      this.userService.user$.pipe(untilDestroyed(this)).subscribe((user) => {
        if (!user) return;
        this.loadOffersForUser(user);
      });
      this.loadManagedUsersAndOffers();
    } else {
      this.loadTrendingData();
    }
  }

  private loadTrendingData(): void {
    this.trendingCategoriesFacade.loadTrendingData();

    // Subscribe to loading state changes
    this.trendingCategoriesFacade.loading$
      .pipe(untilDestroyed(this))
      .subscribe((loading) => {
        if (!loading) {
          this.isLoadingSubject.next(false);
        }
      });
  }

  private loadOffersForUser(user: User): void {
    const categoryIds = user.categories?.map((c) => c.id) ?? [];
    const filter: StructuredSearchFilterDto = {
      fulltext: [],
      categories: categoryIds,
      offerTypes: [],
      limit: 99999,
      offset: 0,
      postalCode: user.zipCode ? [user.zipCode] : null,
    };
    this.offerService
      .searchOffersByStructuredFilter(filter)
      .pipe(
        map((offers) => offers.filter((o) => o.status === 'APPROVED')),
        tap(() => this.isLoadingSubject.next(false)),
      )
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (offers) => {
          this.userOffersSubject.next(offers);
          this.checkAndLoadTrendingData();
        },
        error: () => {
          this.userOffersSubject.next([]);
          this.checkAndLoadTrendingData();
        },
      });
  }

  private loadManagedUsersAndOffers(): void {
    this.userService
      .getManagedUsers()
      .pipe(
        switchMap((users) => {
          if (!users || users.length === 0) {
            this.managedUsersSubject.next([]);
            this.checkAndLoadTrendingData();
            return of([] as ManagedUser[]);
          }
          this.managedUsersSubject.next(users);
          users.forEach((user) => this.loadOffersForManagedUser(user));
          return of(users);
        }),
        tap(() => this.isLoadingSubject.next(false)),
      )
      .pipe(untilDestroyed(this))
      .subscribe();
  }

  private loadOffersForManagedUser(user: ManagedUser): void {
    const categoryIds = user.categories.map((c) => c.id);
    const filter: StructuredSearchFilterDto = {
      fulltext: [],
      categories: categoryIds,
      offerTypes: [],
      limit: 99999,
      offset: 0,
      postalCode: user.zipCode ? [user.zipCode] : null,
    };

    this.offerService
      .searchOffersByStructuredFilter(filter)
      .pipe(map((offers) => offers.filter((o) => o.status === 'APPROVED')))
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (offers) => {
          const current = this.managedUsersOffersSubject.getValue();
          this.managedUsersOffersSubject.next({
            ...current,
            [user.id!]: offers,
          });
        },
        error: () => {
          const current = this.managedUsersOffersSubject.getValue();
          this.managedUsersOffersSubject.next({ ...current, [user.id!]: [] });
        },
      });
  }

  private checkAndLoadTrendingData(): void {
    // Check if we need to load trending data
    const user = this.userService.getUser();
    const managedUsers = this.managedUsersSubject.getValue();

    // Only load trending data if:
    // 1. User is logged in (we're in the authenticated flow)
    // 2. User has no categories OR no managed users
    // 3. We haven't already loaded trending data
    if (
      user &&
      (!user.categories || user.categories.length === 0) &&
      (!managedUsers || managedUsers.length === 0)
    ) {
      // Check if we already have trending data in the store
      this.trendingCategoriesFacade.hasTrendingData$
        .pipe(untilDestroyed(this))
        .subscribe((hasData) => {
          if (!hasData) {
            this.loadTrendingData();
          }
        });
    }
  }

  selectCategory(categoryId: string): void {
    this.trendingCategoriesFacade.selectCategory(categoryId);
  }
}
